// useDetailDrawer — open/closed state for the Document Detail panel
// stack ("drawer"). CTSK-522.
//
// Persistence: one localStorage boolean, `tasko.documents.detail.drawer.v1`,
// holding the operator's *collapsed* choice ("true" = collapsed). Until the
// operator toggles once, nothing is stored and the drawer follows the
// viewport: open at >= 1280px, collapsed below. After the first toggle the
// stored value wins on every viewport.
//
// The individual CollapsiblePanel keys (`panel:collapsed:*`) are separate and
// untouched; while the drawer is collapsed they are simply inert.

import { useCallback, useState } from 'react';
import { useMediaQuery } from './useMediaQuery.js';

export const DETAIL_DRAWER_STORE_KEY = 'tasko.documents.detail.drawer.v1';
export const DETAIL_DRAWER_WIDE_QUERY = '(min-width: 1280px)';

function readStored(): boolean | null {
  try {
    const v = window.localStorage.getItem(DETAIL_DRAWER_STORE_KEY);
    return v === 'true' ? true : v === 'false' ? false : null;
  } catch {
    return null;
  }
}

export function useDetailDrawer(): { open: boolean; toggle: () => void } {
  // fallback=true: where matchMedia is missing (jsdom) treat the viewport as wide.
  const wide = useMediaQuery(DETAIL_DRAWER_WIDE_QUERY, true);
  const [stored, setStored] = useState<boolean | null>(() => readStored());

  const collapsed = stored ?? !wide;

  const toggle = useCallback(() => {
    const next = !collapsed;
    setStored(next);
    try {
      window.localStorage.setItem(DETAIL_DRAWER_STORE_KEY, String(next));
    } catch {
      /* private mode / quota — keep in-memory state only */
    }
  }, [collapsed]);

  return { open: !collapsed, toggle };
}
